import { useEffect, useMemo, useRef, useState } from 'react';
import { getEmbeddedSignupByPhone, updatePasscode } from '../../../api/login.service';
import { getInstagramAccounts, InstagramAccount, updateAiMessageLimit, updateUserInstagramAccount } from '../../../api/instagram.service';
import Switch from '../../../components/switch';
import LoadingComponent from '../../../components/LoadingComponent';
import request from '../../../api/request';
import { KPICard, StatsGrid } from '../../../components/cards/ModernCards';
import PipelineSettings from './PipelineSettings';
import MicrositeSettings from './MicrositeSettings';

interface UserSettingsData {
  event?: string;
  user_email?: string;
  platform_type?: string;
  bot_messages?: string;
  max_bot_messages?: string;
  user_type?: string;
  phoneNumberId?: string;
  utility_messages?: string;
  code_verification_status?: string;
  verified_name?: string;
  quality_rating?: string;
  throughput_level?: string;
  version?: string;
  last_onboarded_time?: string;
  businessId?: string;
  max_utility_messages?: string;
  createdAt?: string;
  marketing_messages?: string;
  display_phone_number?: string;
  updatedAt?: string;
  passcode?: string;
  max_marketing_messages?: string;
  user_status?: string;
  wabaId?: string;
  type?: string;
  bot_settings?: boolean;
  instagramUserId?: string;
  instagramUsername?: string;
  instagramName?: string;
  aiUserDailyLimit?: number;
  aiTotalDailyLimit?: number;
  micrositeUrl?: string;
}

interface BotSettingsResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export default function ModernUserSettings() {
  const [userData, setUserData] = useState<UserSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form states
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [updatingPasscode, setUpdatingPasscode] = useState(false);
  
  // Bot replay toggle state
  const [stopBotReplay, setStopBotReplay] = useState(false);
  const [updatingBotSettings, setUpdatingBotSettings] = useState(false);

  const [instagramAccounts, setInstagramAccounts] = useState<InstagramAccount[]>([]);
  const [instagramQuery, setInstagramQuery] = useState('');
  const [instagramOpen, setInstagramOpen] = useState(false);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [instagramError, setInstagramError] = useState<string | null>(null);
  const [selectedInstagram, setSelectedInstagram] = useState<InstagramAccount | null>(null);
  const [savingInstagram, setSavingInstagram] = useState(false);
  const [aiUserDailyLimit, setAiUserDailyLimit] = useState('10');
  const [aiTotalDailyLimit, setAiTotalDailyLimit] = useState('100');
  const [savingAiLimit, setSavingAiLimit] = useState(false);
  const instagramBoxRef = useRef<HTMLDivElement>(null);

  // Fetch user settings data
  const fetchUserSettings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const phoneNumber = sessionStorage.getItem('phone') || localStorage.getItem('phone');
      console.log('Fetching user settings for phone:', phoneNumber);
      
      if (!phoneNumber) {
        throw new Error('Phone number not found in session storage');
      }

      const response = await getEmbeddedSignupByPhone(phoneNumber);
      console.log('User settings response:', response);
      
      if (response.success && response.records && response.records.length > 0) {
        const userData = response.records[0];
        setUserData(userData);
        setStopBotReplay(!userData.bot_settings);
        setAiUserDailyLimit(String(userData.aiUserDailyLimit ?? 10));
        setAiTotalDailyLimit(String(userData.aiTotalDailyLimit ?? 100));
        if (userData.instagramUserId || userData.instagramUsername) {
          const savedAccount: InstagramAccount = {
            userId: userData.instagramUserId || '',
            igId: '',
            name: userData.instagramName || '',
            username: userData.instagramUsername || '',
            savedAt: '',
            updatedAt: '',
          };
          setSelectedInstagram(savedAccount);
          setInstagramQuery(savedAccount.username ? `@${savedAccount.username}` : savedAccount.name);
        }
        console.log('User data loaded successfully:', userData);
      } else {
        // Create fallback user data if API fails
        const fallbackData: UserSettingsData = {
          user_email: 'user@example.com',
          verified_name: 'User',
          display_phone_number: phoneNumber,
          user_status: 'active',
          bot_settings: false,
          bot_messages: '0',
          max_bot_messages: '500',
          marketing_messages: '0',
          max_marketing_messages: '500',
          utility_messages: '0',
          max_utility_messages: '500',
          platform_type: 'CLOUD_API',
          user_type: 'free',
          code_verification_status: 'VERIFIED',
          quality_rating: 'UNKNOWN',
          throughput_level: 'STANDARD',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setUserData(fallbackData);
        setStopBotReplay(true);
        console.log('Using fallback user data');
      }
      
    } catch (err: any) {
      console.error('Error fetching user settings:', err);
      
      // Create fallback user data on error
      const phoneNumber = sessionStorage.getItem('phone') || localStorage.getItem('phone') || '+1234567890';
      const fallbackData: UserSettingsData = {
        user_email: 'user@example.com',
        verified_name: 'User',
        display_phone_number: phoneNumber,
        user_status: 'active',
        bot_settings: false,
        bot_messages: '0',
        max_bot_messages: '500',
        marketing_messages: '0',
        max_marketing_messages: '500',
        utility_messages: '0',
        max_utility_messages: '500',
        platform_type: 'CLOUD_API',
        user_type: 'free',
        code_verification_status: 'VERIFIED',
        quality_rating: 'UNKNOWN',
        throughput_level: 'STANDARD',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setUserData(fallbackData);
      setStopBotReplay(true);
      setError('Using fallback data - API connection failed');
    } finally {
      setLoading(false);
    }
  };

  // Update passcode
  const handleUpdatePasscode = async () => {
    if (!newPasscode || !confirmPasscode) {
      setError('Please fill in both passcode fields');
      return;
    }

    if (newPasscode !== confirmPasscode) {
      setError('Passcodes do not match');
      return;
    }

    if (newPasscode.length < 4) {
      setError('Passcode must be at least 4 digits');
      return;
    }

    setUpdatingPasscode(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await updatePasscode(newPasscode);
      
      if (response.success) {
        setSuccess('Passcode updated successfully!');
        setNewPasscode('');
        setConfirmPasscode('');
        // Refresh user data to get updated passcode
        await fetchUserSettings();
      } else {
        throw new Error(response.message || 'Failed to update passcode');
      }
      
    } catch (err: any) {
      console.error('Error updating passcode:', err);
      setError(err?.message || 'Failed to update passcode');
    } finally {
      setUpdatingPasscode(false);
    }
  };

  // Handle bot settings update
  const handleBotSettingsUpdate = async (enabled: boolean) => {
    if (!userData?.phoneNumberId) {
      setError('Phone number ID not found');
      return;
    }

    try {
      setUpdatingBotSettings(true);
      setError(null);
      setSuccess(null);

      const response = await request.requestWIthAuth<BotSettingsResponse>({
        url: '/user/bot-settings',
        method: 'PATCH',
        data: {
          phoneNumberId: userData.phoneNumberId,
          bot_settings: enabled
        }
      });

      if (response.data?.success) {
        setStopBotReplay(!enabled);
        setUserData(prev => prev ? { ...prev, bot_settings: enabled } : null);
        setSuccess('Bot settings updated successfully!');
      } else {
        throw new Error(response.data?.message || 'Failed to update bot settings');
      }
      
    } catch (err: any) {
      console.error('Error updating bot settings:', err);
      setError(err?.message || 'Failed to update bot settings');
      setStopBotReplay(!enabled); // Revert on error
    } finally {
      setUpdatingBotSettings(false);
    }
  };

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const handleAiLimitSubmit = async () => {
    const userLimit = Number(aiUserDailyLimit);
    const totalLimit = Number(aiTotalDailyLimit);
    if (!Number.isInteger(userLimit) || userLimit < 1 || !Number.isInteger(totalLimit) || totalLimit < 1) {
      setError('Enter whole numbers of at least 1');
      return;
    }
    if (!userData?.phoneNumberId) {
      setError('Phone number ID not found');
      return;
    }

    try {
      setSavingAiLimit(true);
      setError(null);
      setSuccess(null);
      const response = await updateAiMessageLimit({
        phoneNumberId: userData.phoneNumberId,
        aiUserDailyLimit: userLimit,
        aiTotalDailyLimit: totalLimit,
      });
      if (!response?.success) {
        throw new Error(response?.error || response?.message || 'Failed to update AI message limits');
      }
      setUserData((prev) => prev ? {
        ...prev,
        aiUserDailyLimit: userLimit,
        aiTotalDailyLimit: totalLimit,
      } : prev);
      setSuccess('AI message limits updated successfully!');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update AI message limits');
    } finally {
      setSavingAiLimit(false);
    }
  };

  const selectInstagramAccount = (account: InstagramAccount) => {
    setSelectedInstagram(account);
    setInstagramQuery(account.username ? `@${account.username}` : account.name);
    setInstagramOpen(false);
  };

  const handleInstagramSubmit = async () => {
    if (!selectedInstagram?.userId || !selectedInstagram.username || !selectedInstagram.name) {
      setError('Select an Instagram account first');
      return;
    }
    if (!userData?.phoneNumberId) {
      setError('Phone number ID not found');
      return;
    }

    try {
      setSavingInstagram(true);
      setError(null);
      setSuccess(null);
      const response = await updateUserInstagramAccount({
        phoneNumberId: userData.phoneNumberId,
        userId: selectedInstagram.userId,
        username: selectedInstagram.username,
        name: selectedInstagram.name,
      });
      if (!response?.success) {
        throw new Error(response?.error || response?.message || 'Failed to update Instagram account');
      }
      setUserData((prev) => prev ? {
        ...prev,
        instagramUserId: selectedInstagram.userId,
        instagramUsername: selectedInstagram.username,
        instagramName: selectedInstagram.name,
      } : prev);
      setSuccess('Instagram account updated successfully!');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update Instagram account');
    } finally {
      setSavingInstagram(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setInstagramLoading(true);
    getInstagramAccounts()
      .then((accounts) => {
        if (!cancelled) setInstagramAccounts(accounts);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInstagramError(err?.response?.data?.error || err?.message || 'Could not load Instagram accounts');
        }
      })
      .finally(() => {
        if (!cancelled) setInstagramLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!instagramBoxRef.current?.contains(event.target as Node)) {
        setInstagramOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const filteredInstagramAccounts = useMemo(() => {
    const query = instagramQuery.trim().toLowerCase().replace(/^@/, '');
    if (!query) return instagramAccounts;
    return instagramAccounts.filter((account) => {
      const username = (account.username || '').toLowerCase();
      const name = (account.name || '').toLowerCase();
      return username.includes(query) || name.includes(query);
    });
  }, [instagramAccounts, instagramQuery]);

  if (loading) {
    return <LoadingComponent loading={true} message="Loading user settings..." />;
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
        <p className="text-gray-600">Manage your WhatsApp Business account settings and preferences</p>
      </div>

      <PipelineSettings />
      <MicrositeSettings
        phoneNumberId={userData?.phoneNumberId}
        savedUrl={userData?.micrositeUrl}
      />

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
            <p className="text-green-800 font-medium">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Account Overview Cards */}
      <StatsGrid>
        <KPICard
          title="Account Status"
          value={userData?.user_status || 'Unknown'}
          icon={
            <div className={`w-6 h-6 rounded-full ${userData?.user_status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
          }
        />
        <KPICard
          title="Phone Number"
          value={userData?.display_phone_number || 'N/A'}
          icon={<span className="text-blue-600 text-lg">📱</span>}
        />
        <KPICard
          title="Account Type"
          value={userData?.user_type || 'Unknown'}
          icon={<span className="text-purple-600 text-lg">👤</span>}
        />
        <KPICard
          title="Verification Status"
          value={userData?.code_verification_status || 'Unknown'}
          icon={<span className="text-green-600 text-lg">✅</span>}
        />
      </StatsGrid>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Instagram account</h3>
        <p className="text-sm text-gray-600 mb-4">
          Search by username and choose the Instagram account for this user.
          {selectedInstagram?.name ? ` Selected name: ${selectedInstagram.name}.` : ''}
        </p>

        <div ref={instagramBoxRef} className="relative max-w-xl">
          <input
            type="text"
            value={instagramQuery}
            onChange={(e) => {
              setInstagramQuery(e.target.value);
              setInstagramOpen(true);
            }}
            onFocus={() => setInstagramOpen(true)}
            placeholder="Search Instagram username"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoComplete="off"
          />

          {instagramOpen && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {instagramLoading && (
                <div className="px-3 py-2 text-sm text-gray-500">Loading accounts...</div>
              )}
              {!instagramLoading && instagramError && (
                <div className="px-3 py-2 text-sm text-red-600">{instagramError}</div>
              )}
              {!instagramLoading && !instagramError && filteredInstagramAccounts.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No matching Instagram username</div>
              )}
              {!instagramLoading && filteredInstagramAccounts.map((account) => (
                <button
                  key={account.userId || account.username}
                  type="button"
                  onClick={() => selectInstagramAccount(account)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {account.username ? `@${account.username}` : 'No username'}
                  </span>
                  <span className="text-sm text-gray-500">{account.name || 'Unknown'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleInstagramSubmit}
          disabled={savingInstagram || !selectedInstagram}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {savingInstagram ? 'Saving...' : 'Submit'}
        </button>
      </div>

      {/* Message Usage Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Message Usage</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {userData?.bot_messages || '0'}
            </div>
            <div className="text-sm text-gray-600 mb-1">Bot Messages</div>
            <div className="text-xs text-gray-500">
              Max: {userData?.max_bot_messages || '0'}
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {userData?.marketing_messages || '0'}
            </div>
            <div className="text-sm text-gray-600 mb-1">Marketing Messages</div>
            <div className="text-xs text-gray-500">
              Max: {userData?.max_marketing_messages || '0'}
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 mb-1">
              {userData?.utility_messages || '0'}
            </div>
            <div className="text-sm text-gray-600 mb-1">Utility Messages</div>
            <div className="text-xs text-gray-500">
              Max: {userData?.max_utility_messages || '0'}
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-base font-semibold text-gray-900 mb-1">Daily AI reply limit</h4>
          <p className="text-sm text-gray-600 mb-4">
            Each Instagram customer and each WhatsApp customer has their own count. The total is every Instagram AI reply plus every WhatsApp AI reply today. The count goes up only after the bot sends a reply.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Per customer
              </label>
              <input
                type="number"
                min={1}
                max={10000}
                value={aiUserDailyLimit}
                onChange={(e) => setAiUserDailyLimit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={savingAiLimit}
              />
              <p className="mt-1 text-xs text-gray-500">Default 10 bot replies for one customer in one day.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                All AI replies
              </label>
              <input
                type="number"
                min={1}
                max={10000}
                value={aiTotalDailyLimit}
                onChange={(e) => setAiTotalDailyLimit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={savingAiLimit}
              />
              <p className="mt-1 text-xs text-gray-500">Default 100. Instagram and WhatsApp AI replies added together.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAiLimitSubmit}
            disabled={savingAiLimit}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {savingAiLimit ? 'Saving...' : 'Save limits'}
          </button>
        </div>

        {/* Bot Replay Toggle */}
        <div className="pt-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h4 className="text-base font-semibold text-gray-900 mb-1">
                {stopBotReplay ? "Start Bot Replay" : "Stop Bot Replay"}
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                Toggle to enable or disable bot replay functionality
              </p>
              {updatingBotSettings && (
                <p className="text-sm text-blue-600">Updating bot settings...</p>
              )}
            </div>
            <div className="relative ml-4">
              <Switch 
                color="blue"
                checked={!stopBotReplay}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const enabled = e.target.checked;
                  handleBotSettingsUpdate(enabled);
                }}
                disabled={updatingBotSettings}
              />
              {updatingBotSettings && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Passcode Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Security Settings</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Passcode
            </label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.passcode ? '••••••••' : 'Not set'}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Passcode
              </label>
              <input
                type="password"
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter new passcode"
                disabled={updatingPasscode}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Passcode
              </label>
              <input
                type="password"
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm new passcode"
                disabled={updatingPasscode}
              />
            </div>
          </div>
          
          <button
            onClick={handleUpdatePasscode}
            disabled={updatingPasscode || !newPasscode || !confirmPasscode}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updatingPasscode ? 'Updating...' : 'Update Passcode'}
          </button>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.user_email || 'N/A'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.verified_name || 'N/A'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform Type</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.platform_type || 'N/A'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quality Rating</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.quality_rating || 'N/A'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Created At</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated</label>
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {userData?.updatedAt ? new Date(userData.updatedAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={fetchUserSettings}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Refresh Data
        </button>
      </div>
    </div>
  );
}